/**
 * Vendor brand marks for the Integrations tiles.
 *
 * Generated from the official SVGs rather than redrawn: a logo approximated
 * from memory is worse than no logo, and a hand-copied bezier is a silent
 * corruption nobody reviews.
 *
 * Two changes from the source files, both deliberate:
 *
 *  * Fortnox ships its symbol with a `<style>` block using generic `.cls-N`
 *    class names. SVG `<style>` is DOCUMENT-scoped in HTML, so two inlined
 *    brand SVGs using the same names would fight and whichever rendered second
 *    would recolour the first. The fills are inline here instead.
 *  * The Fortnox `F` is white in the source, which is invisible on a light
 *    card. It uses #003824 — the exact green of Fortnox's own light-background
 *    wordmark — so the palette is theirs, not one we picked.
 *
 * Both marks carry their own colours and ignore `currentColor` on purpose: a
 * brand mark that changes colour with the surrounding text is no longer the
 * brand's mark. They are decorative beside a text label, so they are
 * aria-hidden.
 */

export function FortnoxMark({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 159.76 159.78"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path fill="#003824" d="M79.84,31.22h25.08c1.81,0,2.79.98,2.79,2.79v13.38c0,1.81-.97,2.78-2.79,2.78h-25.08c-5.85,0-9.75,3.9-9.75,9.75v18.25h34.83c1.81,0,2.79.97,2.79,2.78v13.38c0,1.81-.97,2.79-2.79,2.79h-34.83v26.05c0,1.81-.98,2.79-2.79,2.79h-13.93c-1.81,0-2.79-.97-2.79-2.79v-62.7c0-17.41,11.84-29.26,29.26-29.26" />
      <path fill="#007533" d="M79.88,159.78c-15.29,0-30.41-4.38-43.47-12.85-16.18-10.49-28.01-26.47-33.31-44.98-5.25-18.33-3.8-37.93,4.09-55.18,1.26-2.76,4.53-3.98,7.29-2.71,2.76,1.26,3.98,4.53,2.71,7.29-6.8,14.87-8.05,31.77-3.52,47.58,4.57,15.96,14.77,29.74,28.72,38.78,16.55,10.73,36.96,13.85,56,8.54,2.93-.81,5.96.9,6.77,3.82.81,2.93-.9,5.96-3.82,6.77-7.06,1.97-14.28,2.93-21.47,2.93Z" />
      <path fill="#ffc200" d="M119.89,148.12c-1.82,0-3.6-.9-4.64-2.55-1.63-2.56-.88-5.96,1.68-7.59,8.36-5.33,15.35-12.25,20.75-20.59,20.67-31.87,11.56-74.62-20.31-95.29-1.04-.68-2.18-1.36-3.58-2.15-2.64-1.5-3.57-4.85-2.07-7.5,1.5-2.64,4.85-3.57,7.5-2.07,1.59.9,2.91,1.69,4.14,2.49,36.96,23.97,47.52,73.54,23.55,110.5-6.27,9.67-14.37,17.7-24.07,23.88-.92.58-1.94.86-2.95.86Z" />
      <path fill="#00db33" d="M24.12,36.17c-1.29,0-2.59-.45-3.63-1.37-2.28-2.01-2.5-5.48-.49-7.76C39.21,5.21,68.82-4.42,97.26,1.92c2.97.66,4.83,3.6,4.17,6.56-.66,2.96-3.6,4.83-6.56,4.17-24.53-5.46-50.06,2.84-66.63,21.65-1.09,1.23-2.6,1.87-4.13,1.87Z" />
    </svg>
  );
}

export function BokioMark({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path fill="#1259EF" d="M0 0h40v40H0z" />
      <path fill="#fff" d="M25.825 17.472c.571-1.056.892-2.307.892-3.691 0-3.854-3.496-7.115-7.635-7.115h-7.416v26.666h2.425c1.256 0 2.614-.011 3.931-.016 1.143-.006 2.228-.018 3.097-.018 4.896 0 8.88-3.84 8.88-8.562 0-3.06-1.667-5.748-4.174-7.262zm-9.31-6.115 2.567-.017c1.75 0 2.793 1.166 2.793 2.441 0 2.263-1.495 2.394-1.929 2.463-.758.126-2.253.327-3.431.918zm4.604 17.268c-.888 0-1.978.006-3.133.017-.488.006-.983.006-1.471.013v-3.923c0-1.998 2.24-3.893 4.604-3.893 2.227 0 4.038 1.746 4.038 3.893 0 2.148-1.811 3.893-4.037 3.893z" />
    </svg>
  );
}

/**
 * Oqto's own icon (app.oqto.se/assets/oqto-icon.svg, pointed at by Erik on
 * 2026-09-24), reproduced path for path in its own orange. Also the file
 * under public/logos/oqto.svg.
 */
export function OqtoMark({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 143 143"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <path fill="#FF6319" d="M117.74 96.8434C114.329 96.8791 111.097 96.2964 108.282 95.2262C106.028 94.3701 103.917 93.0383 101.985 91.4449C98.7886 88.817 98.2519 84.2271 100.542 80.7907C105.241 73.7869 107.972 65.3682 107.972 56.2954C107.972 30.8845 89.5336 12.0731 63.6766 12.1801H63.6408C37.7958 12.085 19.8224 30.8845 19.8224 56.2954C19.8224 65.3682 22.5655 73.7869 27.2527 80.7907C29.5545 84.2152 29.0059 88.817 25.8096 91.4449C23.8775 93.0383 21.7664 94.3582 19.5123 95.2262C16.6976 96.2845 13.4655 96.8791 10.0545 96.8434C3.11319 96.772 -1.41893 104.156 1.45538 110.458C3.10126 114.061 6.7031 116.356 10.6747 116.356C11.0921 116.356 11.4976 116.356 11.927 116.333C16.8646 116.166 21.7903 115.227 26.5132 113.407C31.594 111.457 36.0068 108.52 39.7518 105.226C41.6124 103.586 44.3674 105.678 43.2344 107.878C43.0674 108.199 42.9004 108.52 42.7096 108.841C39.5252 114.489 34.1582 119.745 27.0738 123.217C20.4068 126.464 19.6435 135.62 25.7619 139.793L26.0481 139.972C28.9582 141.827 32.6555 142.148 35.7564 140.649C45.8344 135.774 52.7399 127.141 58.2619 117.355C61.184 112.29 60.8023 107.961 63.8913 107.652C66.9803 107.961 66.6106 112.29 69.5207 117.355C75.0427 127.13 81.9482 135.774 92.0262 140.649C95.1391 142.148 98.8244 141.827 101.735 139.972L102.021 139.793C108.139 135.62 107.364 126.464 100.709 123.217C93.6244 119.745 88.2574 114.489 85.073 108.841C84.8941 108.52 84.7152 108.199 84.5482 107.878C83.4152 105.666 86.1703 103.586 88.0308 105.226C91.7758 108.52 96.1767 111.457 101.269 113.407C105.992 115.227 110.906 116.178 115.856 116.333C116.285 116.356 116.702 116.356 117.108 116.356C121.08 116.356 124.681 114.061 126.327 110.458C129.202 104.156 124.669 96.772 117.728 96.8434H117.74Z" />
      <path fill="#FF6319" d="M127.22 47.7383C135.664 47.7383 142.51 40.9132 142.51 32.4941C142.51 24.075 135.664 17.25 127.22 17.25C118.775 17.25 111.93 24.075 111.93 32.4941C111.93 40.9132 118.775 47.7383 127.22 47.7383Z" />
      <path fill="#FF6319" d="M104.285 18.3596C109.371 18.3596 113.493 14.2496 113.493 9.17978C113.493 4.10993 109.371 0 104.285 0C99.2004 0 95.0781 4.10993 95.0781 9.17978C95.0781 14.2496 99.2004 18.3596 104.285 18.3596Z" />
    </svg>
  );
}

/**
 * Vitec has no official symbol we hold a licence to redistribute, so this is
 * a plain text mark — a "V" in Vitec's dark blue on a rounded tile — rather
 * than a redrawn logo. Replace with the official SVG once Vitec supplies one
 * under the partner agreement.
 */
export function VitecMark({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="40" height="40" rx="6" fill="#0B2A5B" />
      <path fill="#fff" d="M9 11h5.2l5.8 14.3L25.8 11H31l-8.6 20h-4.8z" />
    </svg>
  );
}

/**
 * Spiris's developer terms (§2.6) allow its logo only with written approval,
 * which we do not hold yet, so this is a plain text mark — an "S" on a dark
 * green tile — rather than a redrawn logo. Replace with the official SVG once
 * Spiris approves its use.
 */
export function SpirisMark({ className }: { readonly className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      focusable="false"
    >
      <rect width="40" height="40" rx="6" fill="#0E3B2E" />
      <text x="20" y="28" textAnchor="middle" fontFamily="Helvetica, Arial, sans-serif" fontSize="22" fontWeight="700" fill="#fff">S</text>
    </svg>
  );
}
